import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Science } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import { getGradientCss, useColorMode } from '../themeFromConfig';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user, login } = useAuth();
  const { config } = useConfig();
  const { mode } = useColorMode();
  const navigate = useNavigate();
  const isDark = mode === 'dark';
  const gradientLogin = getGradientCss(config?.paleta, 'gradientLogin');
  const gradientCard = isDark
    ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)'
    : getGradientCss(config?.paleta, 'gradientCard');

  useEffect(() => {
    if (user) {
      if (user.rol === 'bioanalista') {
        navigate('/ordenes', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      setSubmitting(true);
      await login(username.trim(), password);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Error al iniciar sesión. Verifica que el backend esté en ejecución.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: gradientLogin,
        py: 3,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: (t) => `0 12px 40px ${t.palette.primary.dark}33`,
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              mb: 3,
              py: 2,
              px: 2,
              borderRadius: 2,
              background: gradientCard,
            }}
          >
            {/* Placa clara detrás del logo: los logos de marca suelen ser oscuros */}
            {config?.logo_url ? (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#FFFFFF',
                  borderRadius: 2,
                  px: 2.5,
                  py: 1.5,
                  mb: 2,
                  boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.35)' : '0 1px 4px rgba(0,0,0,0.08)',
                }}
              >
                <Box
                  component="img"
                  src={config?.logo_url}
                  alt={`Logo de ${config?.nombre || 'laboratorio'}`}
                  sx={{ maxWidth: 180, maxHeight: 100, objectFit: 'contain', display: 'block' }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: gradientLogin,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
                aria-hidden
              >
                <Science sx={{ fontSize: 36, color: 'common.white' }} />
              </Box>
            )}
            <Typography
              variant="h5"
              component="h1"
              fontWeight={700}
              sx={{
                color: (t) => (t.palette.mode === 'dark' ? t.palette.primary.light : t.palette.primary.dark),
              }}
            >
              {config?.nombre || 'Laboratorio Clínico'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: (t) =>
                  t.palette.mode === 'dark' ? 'rgba(255,255,255,0.72)' : t.palette.text.secondary,
              }}
            >
              Inicie sesión para continuar
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Usuario"
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              autoFocus
              autoComplete="username"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              label="Contraseña"
              type="password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              InputLabelProps={{ shrink: true }}
              sx={{
                mb: 2,
                // Evita que el fondo amarillo del autofill tape el borde/label en Chrome
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: (t) => `0 0 0 100px ${t.palette.background.paper} inset`,
                  WebkitTextFillColor: (t) => t.palette.text.primary,
                  caretColor: (t) => t.palette.text.primary,
                  borderRadius: 'inherit',
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{
                mt: 1,
                mb: 2,
                py: 1.5,
                background: gradientLogin,
                '&:hover': { opacity: 0.95 },
              }}
            >
              {submitting ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </Button>
          </form>

          <Box sx={{ mt: 3, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
            <Typography
              variant="caption"
              display="block"
              sx={{
                fontWeight: 500,
                color: (t) =>
                  t.palette.mode === 'dark' ? 'rgba(255,255,255,0.72)' : t.palette.text.secondary,
              }}
            >
              Diseño y Programación
            </Typography>
            <Typography
              variant="caption"
              display="block"
              sx={{
                color: (t) =>
                  t.palette.mode === 'dark' ? 'rgba(255,255,255,0.72)' : t.palette.text.secondary,
              }}
            >
              Ing. Luis Espinales
            </Typography>
            <Typography
              variant="caption"
              display="block"
              sx={{
                color: (t) =>
                  t.palette.mode === 'dark' ? 'rgba(255,255,255,0.85)' : t.palette.text.secondary,
              }}
            >
              Tel: +505 57276091 | Email: leylr88@gmail.com
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
